import type { Address, PlaceOfServiceType, ProviderType, SupportProviderAutomation } from '../../api';

/**
 * The Roster screen's own view of a provider and a facility (FE-109).
 *
 * Shaped after `SupportProvider` / `SupportFacility` and built from the same API enums and
 * `Address`, so the real adapter is a field-for-field mapping rather than a translation. It is a
 * separate type rather than the generated model because the screen needs a few things the model
 * does not guarantee — an `id` that is always present, `tags` and `addresses` that are always
 * arrays — and because the generated client is not edited for feature work (NS-16).
 */

export type RosterRole = 'client' | 'ops';

export type RosterTab = 'providers' | 'facilities';

export interface RosterTag {
    id: string;
    name: string;
}

export interface RosterProvider {
    id: string;
    type: ProviderType;
    organization?: string;
    first?: string;
    middle?: string;
    last?: string;
    suffix?: string;
    npi?: string;
    ein?: string;
    ssn?: string;
    /** Medicare number. */
    ptan?: string;
    license?: string;
    taxonomyCode?: string;
    taxonomyQualifier?: string;
    email?: string;
    phone?: string;
    fax?: string;
    tags: RosterTag[];
    halt: boolean;
    automation?: SupportProviderAutomation;
    addresses: Address[];
    created?: string;
    /**
     * Last modification time, ISO 8601.
     *
     * **The API has no such field** — `SupportProvider` carries `created` and `revisions[]` only
     * (checked 2026-09-24). The mock fills it; the real adapter leaves it undefined and reports
     * `capabilities.updatedTimestamp = false`, so the column can say so instead of rendering a
     * blank that reads as "never updated" (NS-3, NS-4).
     */
    updated?: string;
}

export interface RosterFacility {
    id: string;
    name: string;
    nickname?: string;
    description?: string;
    npi?: string;
    ein?: string;
    ssn?: string;
    /** `SupportFacility.other` — shown as "Other ID". */
    other?: string;
    placeOfServiceCode?: PlaceOfServiceType;
    tags: RosterTag[];
    addresses: Address[];
    created?: string;
    /** See `RosterProvider.updated` — not on the API. */
    updated?: string;
}

export type RosterRecord = RosterProvider | RosterFacility;

/** What the drawer saves. No `id` means create. Tags are not part of it — the update payloads have no tags field. */
export type RosterProviderDraft = Omit<RosterProvider, 'id' | 'tags' | 'created' | 'updated'> & { id?: string };

export type RosterFacilityDraft = Omit<RosterFacility, 'id' | 'tags' | 'created' | 'updated'> & { id?: string };

export type RosterSortDirection = 'asc' | 'desc';

export interface RosterSort {
    /** A column key from `roster.columns.ts`. */
    key: string;
    direction: RosterSortDirection;
}

export interface RosterListQuery {
    search?: string;
    /** Providers only. Undefined means every type. */
    providerType?: ProviderType;
    sort?: RosterSort;
    offset: number;
    limit: number;
}

export interface RosterPage<T> {
    items: T[];
    /** Every record matching the query, not just this page. */
    total: number;
    /**
     * Set when the source could not return every match — a search field matched more records than
     * one request can carry. The page is then a subset, and the UI must say so (NS-3).
     */
    truncated?: boolean;
}

export interface RosterTypeCounts {
    total: number;
    byType: Partial<Record<ProviderType, number>>;
}

/**
 * A claim as the "apply changes to existing claims" picker shows it.
 *
 * `status` is the design's four-value vocabulary, not a field of `SupportClaim` — the real claim
 * model spreads this across `transmissionStatus`, `claimResponsibilityType` and payments. How
 * those map onto "paid, so locked" is a question for the real adapter, not something to guess
 * here.
 */
/**
 * A claim's state. The mock uses the design's four; real claims carry the API's
 * `ClaimTransmissionStatusType` as is, and `NOT_SENT` when they have none yet — never a status
 * mapped onto the design's words (NS-3).
 */
export type RosterClaimStatus =
    | 'BILLED'
    | 'ACKNOWLEDGED'
    | 'DENIED'
    | 'PAID'
    | 'NOT_SENT'
    | 'SUBMITTED'
    | 'ADJUDICATED'
    | 'COMPLETED'
    | 'REJECTED'
    | 'FORWARDED';

export interface RosterClaim {
    id: string;
    /** Date of service, `YYYY-MM-DD`. */
    dos: string;
    patientName: string;
    payerName: string;
    /** Decimal string, as `Amount.amount` carries it. */
    chargeAmount: string;
    status: RosterClaimStatus;
}

/** Inclusive date-of-service bounds, `YYYY-MM-DD`. A missing bound is open. */
export interface RosterDosRange {
    from?: string;
    to?: string;
}

export interface RosterRecordRef {
    tab: RosterTab;
    id: string;
}

export interface RosterApplyResult {
    updated: number;
    queuedForTransmission: number;
}

/**
 * What the active data source can actually do. The UI reads this instead of assuming, so an
 * affordance the API cannot back ships disabled with a "coming soon" hint (NS-4).
 */
/** A Roster action with its own endpoint — the unit in which an API can lack something. */
export type RosterAction = 'create' | 'update' | 'disable' | 'tag' | 'applyToClaims';

export interface RosterCapabilities {
    /** Create, update, disable and tag writes. The provider app's API is read-only (probed 2026-09-29). */
    write: boolean;
    /** Re-applying a saved record to existing claims, and retransmitting them. */
    applyToClaims: boolean;
    /** A real `updated` timestamp on records. */
    updatedTimestamp: boolean;
    /**
     * Tags on records, and a way to add them. The provider app's API has neither (no `tags` on its
     * models, no `/provider/tag` route — probed 2026-09-29), so there the column and "Add tag" are
     * left out rather than shown empty.
     */
    tags: boolean;
    /**
     * Actions the screen shows but cannot run yet: their endpoint is missing. Each stays visible,
     * disabled, under a "Coming soon" hint, and nothing is sent — NS-4's "disabled with a coming
     * soon hint", not a request that fails on click.
     */
    comingSoon?: ReadonlyArray<RosterAction>;
}

/** Whether `action` is shown but not available yet (see `RosterCapabilities.comingSoon`). */
export function rosterComingSoon(capabilities: RosterCapabilities, action: RosterAction): boolean {
    return !!capabilities.comingSoon?.includes(action);
}

/**
 * Everything the Roster screen reads or writes goes through this — the mock now, the support API
 * later (plan step 9) — so swapping the source never touches the UI.
 */
export interface RosterDataSource {
    readonly kind: 'mock' | 'api';
    readonly capabilities: RosterCapabilities;
    listProviders(query: RosterListQuery): Promise<RosterPage<RosterProvider>>;
    listFacilities(query: RosterListQuery): Promise<RosterPage<RosterFacility>>;
    /**
     * The records with these ids, in no particular order; ids that no longer exist are left out.
     * "Export selected" needs it: a selection spans pages and can outlive the search that found it.
     * The support API can serve it with `filterId`.
     */
    listByIds(tab: RosterTab, ids: string[]): Promise<RosterRecord[]>;
    /** Counts for the type chips — over ALL providers, independent of search. */
    countProvidersByType(): Promise<RosterTypeCounts>;
    saveProvider(draft: RosterProviderDraft): Promise<RosterProvider>;
    saveFacility(draft: RosterFacilityDraft): Promise<RosterFacility>;
    /** Soft delete. The API's only delete is `disableSupportProvider` / `disableSupportFacility`. */
    disable(tab: RosterTab, ids: string[]): Promise<void>;
    addTag(tab: RosterTab, ids: string[], tagName: string): Promise<void>;
    /** Whether the table may offer a sort on this column — the API sorts only on the fields it indexes. */
    canSort(tab: RosterTab, key: string): boolean;
    /** Every claim that references the record, newest date of service first. */
    listClaimsFor(ref: RosterRecordRef): Promise<RosterClaim[]>;
    applyToClaims(
        ref: RosterRecordRef,
        claimIds: string[],
        options: { retransmit: boolean },
    ): Promise<RosterApplyResult>;
}
